import React from "react";
import DropDeskIcon from "@dropDesk/storage/icons";
import { ICONS_DROPDESK } from "@dropDesk/storage/icons/icons.d";
import {
    Badge,
    BadgeRow,
    BenefitDescription,
    BenefitIcon,
    BenefitItem,
    BenefitList,
    BenefitText,
    BenefitTitle,
    Body,
    ButtonPrimary,
    ButtonSecondary,
    Callout,
    CalloutText,
    CheckItem,
    Checklist,
    CheckText,
    CloseButton,
    Container,
    FeatureCard,
    FeatureContent,
    FeatureImage,
    FeatureSubtitle,
    FeatureTitle,
    Footer,
    InvestmentBold,
    InvestmentCallout,
    InvestmentText,
    LeftColumn,
    LeftDescription,
    LeftTitle,
    MegaphoneWrapper,
    PriceBox,
    PriceLabel,
    PriceNote,
    PricePeriod,
    PriceValue,
    RightColumn,
    RightIntro,
    RightIntroSubtitle,
    RightIntroText,
    RightIntroTitle,
    TitleAccent,
} from "@dropDesk/presentation/pages/dashboard/ui/modals/styled";
import { OrangeAppColors, PurpleAppColors, SharedColors } from "@dropDesk/domain/entities/theme/app_colors";
import { ColorScheme } from "@dropDesk/domain/entities/theme/color_scheme";
import imageApp from "@dropDesk/storage/images/image_app.png";
import chatInternoBanner from "@dropDesk/storage/images/chat_interno_banner.png";

const BENEFITS = [
    {
        icon: ICONS_DROPDESK.mobile,
        title: "Atenda de qualquer lugar",
        description: "Converse com seus clientes pelo celular com agilidade e praticidade.",
        colorKey: "accent" as const,
    },
    {
        icon: ICONS_DROPDESK.thunder,
        title: "Centralize e organize",
        description: "Tenha todas as conversas e informações em um só lugar, com segurança.",
        colorKey: "purple" as const,
    },
    {
        icon: ICONS_DROPDESK.growth,
        title: "Mais produtividade",
        description: "Responda mais rápido, acompanhe sua equipe e entregue uma experiência incrível.",
        colorKey: "success" as const,
    },
    {
        icon: ICONS_DROPDESK.star,
        title: "Recursos que impulsionam",
        description: "Ferramentas inteligentes para transformar atendimentos em resultados.",
        colorKey: "orange" as const,
    },
];

const APP_CHECKS = [
    "Atenda de onde estiver",
    "Notificações em tempo real",
    "Experiência completa na palma da mão",
];

const CHAT_CHECKS = [
    "Conversas privadas e em grupo",
    "Compartilhe informações e arquivos",
    "Mais alinhamento e produtividade",
];

type Props = {
    colors: ColorScheme;
    isAdmin: boolean;
    onClose: () => void;
    onEnjoy: () => void;
};

const ModalReleasesContent = ({ colors, isAdmin, onClose, onEnjoy }: Props) => {
    const getBenefitColors = (colorKey: typeof BENEFITS[number]["colorKey"]) => {
        if (colorKey === "accent") {
            return { color: colors.accent, background: `${colors.accent}18` };
        }
        if (colorKey === "purple") {
            return { color: PurpleAppColors.accent, background: `${PurpleAppColors.accent}18` };
        }
        if (colorKey === "success") {
            return { color: colors.success, background: `${colors.success}18` };
        }
        return { color: OrangeAppColors.accent, background: `${OrangeAppColors.accent}18` };
    };

    return (
        <Container background={colors.onBackground}>
            <CloseButton
                color={colors.hint}
                hoverBackground={colors.buttonOnBackground}
                onClick={onClose}
            >
                <DropDeskIcon
                    icon={ICONS_DROPDESK.close}
                    size={16}
                    color={colors.hint}
                />
            </CloseButton>

            <Body>
                <LeftColumn
                    background={`${colors.delicateBackground}34`}
                    border={colors.border}
                >
                    <BadgeRow>
                        <Badge background={colors.accent} color={SharedColors.white}>
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.star}
                                size={12}
                                color={SharedColors.white}
                            />
                            Novidade
                        </Badge>
                        <MegaphoneWrapper color={colors.accent}>
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.megaphone}
                                size={44}
                                color={colors.accent}
                            />
                        </MegaphoneWrapper>
                    </BadgeRow>

                    <LeftTitle color={colors.text}>
                        Mais mobilidade, mais conexão,{" "}
                        <TitleAccent color={colors.accent}>mais resultados!</TitleAccent>
                    </LeftTitle>

                    <LeftDescription color={colors.hintInverse}>
                        Para atender ainda melhor o seu negócio, lançamos novos recursos que
                        tornam seu atendimento mais ágil, conectado e eficiente — onde e quando
                        você precisar.
                    </LeftDescription>

                    <BenefitList>
                        {BENEFITS.map((benefit) => {
                            const benefitColors = getBenefitColors(benefit.colorKey);
                            return (
                                <BenefitItem key={benefit.title}>
                                    <BenefitIcon
                                        background={benefitColors.background}
                                        color={benefitColors.color}
                                    >
                                        <DropDeskIcon
                                            icon={benefit.icon}
                                            size={16}
                                            color={benefitColors.color}
                                        />
                                    </BenefitIcon>
                                    <BenefitText>
                                        <BenefitTitle color={colors.text}>
                                            {benefit.title}
                                        </BenefitTitle>
                                        <BenefitDescription color={colors.hintInverse}>
                                            {benefit.description}
                                        </BenefitDescription>
                                    </BenefitText>
                                </BenefitItem>
                            );
                        })}
                    </BenefitList>

                    <Callout
                        background={`${colors.accent}12`}
                        border={`${colors.accent}28`}
                    >
                        <DropDeskIcon
                            icon={ICONS_DROPDESK.padlock}
                            size={18}
                            color={colors.accent}
                        />
                        <CalloutText color={colors.text}>
                            Segurança, estabilidade e suporte que você já conhece,{" "}
                            <TitleAccent color={colors.accent}>
                                agora com ainda mais valor para o seu negócio.
                            </TitleAccent>
                        </CalloutText>
                    </Callout>
                </LeftColumn>

                <RightColumn>
                    <RightIntro>
                        <DropDeskIcon
                            icon={ICONS_DROPDESK.star}
                            size={16}
                            color={colors.accent}
                        />
                        <RightIntroText>
                            <RightIntroTitle color={colors.text}>
                                Duas novidades, o mesmo compromisso:
                            </RightIntroTitle>
                            <RightIntroSubtitle color={colors.text}>
                                facilitar seu dia a dia e fazer seu negócio crescer.
                            </RightIntroSubtitle>
                        </RightIntroText>
                    </RightIntro>

                    <FeatureCard
                        border={`${colors.accent}55`}
                        background={colors.onBackground}
                    >
                        <FeatureImage src={imageApp} alt="App DropDesk" />
                        <FeatureContent>
                            <FeatureTitle color={colors.text}>App DropDesk</FeatureTitle>
                            <FeatureSubtitle color={colors.accent}>
                                Atenda seus clientes pelo celular
                            </FeatureSubtitle>
                            <Checklist>
                                {APP_CHECKS.map((item) => (
                                    <CheckItem key={item}>
                                        <DropDeskIcon
                                            icon={ICONS_DROPDESK.check}
                                            size={12}
                                            color={colors.accent}
                                        />
                                        <CheckText color={colors.text}>{item}</CheckText>
                                    </CheckItem>
                                ))}
                            </Checklist>
                        </FeatureContent>
                        <PriceBox>
                            <PriceLabel color={colors.hintInverse}>Por apenas</PriceLabel>
                            <PriceValue color={colors.accent}>R$ 29,90</PriceValue>
                            <PricePeriod color={colors.hintInverse}>por mês</PricePeriod>
                            <PriceNote
                                background={`${colors.accent}12`}
                                color={colors.accent}
                            >
                                <DropDeskIcon
                                    icon={ICONS_DROPDESK.check}
                                    size={11}
                                    color={colors.accent}
                                />
                                Por empresa (todos os usuários)
                            </PriceNote>
                        </PriceBox>
                    </FeatureCard>

                    <FeatureCard
                        border={`${colors.delicateBackground}`}
                        background={`${colors.delicateBackground}12`}
                    >
                        <FeatureImage src={chatInternoBanner} alt="Chat Interno" />
                        <FeatureContent>
                            <FeatureTitle color={colors.text}>Chat Interno</FeatureTitle>
                            <FeatureSubtitle color={colors.success}>
                                Comunique sua equipe com eficiência
                            </FeatureSubtitle>
                            <Checklist>
                                {CHAT_CHECKS.map((item) => (
                                    <CheckItem key={item}>
                                        <DropDeskIcon
                                            icon={ICONS_DROPDESK.check}
                                            size={12}
                                            color={colors.success}
                                        />
                                        <CheckText color={colors.text}>{item}</CheckText>
                                    </CheckItem>
                                ))}
                            </Checklist>
                        </FeatureContent>
                        <PriceBox>
                            <PriceLabel color={colors.hintInverse}>Por apenas</PriceLabel>
                            <PriceValue color={colors.success}>R$ 29,90</PriceValue>
                            <PricePeriod color={colors.hintInverse}>por mês</PricePeriod>
                            <PriceNote
                                background={`${colors.success}12`}
                                color={colors.success}
                            >
                                <DropDeskIcon
                                    icon={ICONS_DROPDESK.check}
                                    size={11}
                                    color={colors.success}
                                />
                                Por empresa (todos os usuários)
                            </PriceNote>
                        </PriceBox>
                    </FeatureCard>

                    <InvestmentCallout
                        background={`${colors.accent}10`}
                        border={`${colors.accent}22`}
                    >
                        <DropDeskIcon
                            icon={ICONS_DROPDESK.review}
                            size={18}
                            color={colors.accent}
                        />
                        <InvestmentText color={colors.text}>
                            <InvestmentBold color={colors.accent}>
                                Pequeno investimento, grande impacto.
                            </InvestmentBold>
                            Ferramentas pensadas para dar mais mobilidade, colaboração e
                            resultado ao seu atendimento — com o padrão DropDesk que você já
                            confia.
                        </InvestmentText>
                    </InvestmentCallout>

                    <Footer background={colors.onBackground} border={colors.border}>
                        {isAdmin ? (
                            <>
                                <ButtonSecondary
                                    color={colors.text}
                                    border={colors.border}
                                    hoverBackground={colors.buttonOnBackground}
                                    onClick={onClose}
                                >
                                    Talvez depois
                                </ButtonSecondary>
                                <ButtonPrimary
                                    background={colors.accent}
                                    color={colors.textOnAccent}
                                    onClick={onEnjoy}
                                >
                                    Quero aproveitar!
                                </ButtonPrimary>
                            </>
                        ) : (
                            <ButtonPrimary
                                background={colors.accent}
                                color={colors.textOnAccent}
                                onClick={onClose}
                            >
                                Fechar
                            </ButtonPrimary>
                        )}
                    </Footer>
                </RightColumn>
            </Body>
        </Container>
    );
};

export default ModalReleasesContent;
